// TODO(brand): migrate to <Button>
import React, { useRef, useMemo, useState } from 'react';
import { Team, Player, GameEvent, ViewState, StatRecord, MatchRecord, Organization, isGameType, DEFAULT_STAT_THRESHOLDS } from '../types';
import { Users, Trophy, Calendar, ArrowRight, ClipboardList, MapPin, Clock, TrendingUp, Activity, Shield, Bell, BarChart2, Zap, X, Lock } from 'lucide-react';
import { getBillingStatus, TRIAL_CREDIT_LIMIT } from '../services/supabase';
import { formatDate, formatDateWithWeekday } from '../lib/dates';

interface DashboardProps {
  organizationName: string;
  currentOrg?: Organization;
  teams: Team[];
  players: Player[];
  events: GameEvent[];
  matchRecords?: MatchRecord[];
  changeView: (view: ViewState) => void;
  onPlayGame?: (matchId: string) => void;
  setTeams: React.Dispatch<React.SetStateAction<Team[]>>;
  setPlayers: React.Dispatch<React.SetStateAction<Player[]>>;
  setEvents: React.Dispatch<React.SetStateAction<GameEvent[]>>;
  pendingApprovalsCount?: number;
  onGoToApprovals?: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({
  organizationName,
  currentOrg,
  teams,
  players,
  events,
  matchRecords = [],
  changeView,
  onPlayGame,
  setTeams,
  setPlayers,
  setEvents,
  pendingApprovalsCount = 0,
  onGoToApprovals,
}) => {
  const [showPaywall, setShowPaywall] = useState(false);

  // Helper to safely parse date strings (YYYY-MM-DD or YYYY-M-D) into Date objects at midnight
  const getEventDate = (dateStr: string) => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts.length === 3) {
       const y = parseInt(parts[0]);
       const m = parseInt(parts[1]) - 1;
       const d = parseInt(parts[2]);
       return new Date(y, m, d);
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDateDisplay = (dateStr: string) => {
    const d = getEventDate(dateStr);
    if (!d) return dateStr;
    return formatDateWithWeekday(d);
  };

  const upcomingEvents = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return (events || [])
      .filter(e => {
          if (!e.date) return false;
          const evtDate = getEventDate(e.date);
          if (!evtDate) return false;
          return evtDate.getTime() >= now.getTime();
      })
      .sort((a, b) => {
          const dateA = getEventDate(a.date);
          const dateB = getEventDate(b.date);
          if (!dateA || !dateB) return 0;
          const timeDiff = dateA.getTime() - dateB.getTime();
          if (timeDiff !== 0) return timeDiff;
          return (a.time || '').localeCompare(b.time || '');
      })
      .slice(0, 5);
  }, [events]);

  const nextEvent = upcomingEvents[0];

  const getPlayerCount = (teamId: string) => players.filter(p => p.teamId === teamId).length;
  const getUnassignedCount = () => players.filter(p => !p.teamId || p.teamId === 'unassigned').length;

  const handleEventClick = (event: GameEvent) => {
    const title = event.title.toLowerCase();
    if (title.includes('tryout')) {
      changeView(ViewState.TEAM);
    } else if (event.type === 'practice') {
      changeView(ViewState.PRACTICE);
    } else if (isGameType(event.type)) {
      handlePlayGame(event.id);
    } else {
      changeView(ViewState.SCHEDULE);
    }
  };

  // --- STATS LOGIC ---
  // Compute team totals + per-player totals in one pass over all game events.
  const statsData = useMemo(() => {
    const MIN_ATTACKS  = 20;
    const MIN_SERVES   = 20;
    const MIN_RECEIVES = 20;

    type PlayerTotals = {
      kills: number; attackErrors: number; attackAttempts: number;
      aces: number; serveErrors: number; serveAttempts: number;
      receptions: number; receptionSum: number;
    };

    const playerTotals: Record<string, PlayerTotals> = {};
    let teamKills = 0, teamErrors = 0, teamAttempts = 0;
    let teamServeAttempts = 0, teamServeErrors = 0;
    let teamReceptions = 0, teamReceptionSum = 0;

    for (const event of events || []) {
      if (!isGameType(event.type)) continue;
      const recordStats = matchRecords.find(r => r.eventId === event.id && r.isRecord)?.gameState.stats;
      const statsToProcess = recordStats?.length ? recordStats : (event.stats ?? []);
      if (!statsToProcess.length) continue;

      for (const record of statsToProcess) {
        if (!playerTotals[record.playerId]) {
          playerTotals[record.playerId] = { kills: 0, attackErrors: 0, attackAttempts: 0, aces: 0, serveErrors: 0, serveAttempts: 0, receptions: 0, receptionSum: 0 };
        }
        const p = playerTotals[record.playerId];
        switch (record.statType) {
          case 'kill':          p.kills++;          p.attackAttempts++; teamKills++;          teamAttempts++;        break;
          case 'attack_error':  p.attackErrors++;   p.attackAttempts++; teamErrors++;         teamAttempts++;        break;
          case 'attack_attempt':                    p.attackAttempts++;                       teamAttempts++;        break;
          case 'ace':           p.aces++;           p.serveAttempts++;                        teamServeAttempts++;   break;
          case 'serve_error':   p.serveErrors++;    p.serveAttempts++;  teamServeErrors++;    teamServeAttempts++;   break;
          case 'serve_attempt':                     p.serveAttempts++;                        teamServeAttempts++;   break;
          case 'reception':
            p.receptions++; teamReceptions++;
            if (record.passRating !== undefined) { p.receptionSum += record.passRating; teamReceptionSum += record.passRating; }
            break;
          case 'reception_error':
            p.receptions++; teamReceptions++;
            break;
        }
      }
    }

    const hasAnyData = teamAttempts > 0 || teamServeAttempts > 0 || teamReceptions > 0;

    const teamStats = {
      hasAnyData,
      hitEfficiency: teamAttempts > 0 ? (teamKills - teamErrors) / teamAttempts : null,
      servePct:      teamServeAttempts > 0 ? (teamServeAttempts - teamServeErrors) / teamServeAttempts : null,
      passAvg:       teamReceptions > 0 ? teamReceptionSum / teamReceptions : null,
    };

    const playerMap = new Map(players.map(p => [p.id, p]));
    const qualified = Object.entries(playerTotals)
      .map(([id, s]) => ({ player: playerMap.get(id), ...s }))
      .filter((x): x is { player: Player } & PlayerTotals => !!x.player);

    const hitter = qualified
      .filter(p => p.attackAttempts >= MIN_ATTACKS)
      .sort((a, b) => ((b.kills - b.attackErrors) / b.attackAttempts) - ((a.kills - a.attackErrors) / a.attackAttempts))[0] ?? null;

    const server = qualified
      .filter(p => p.serveAttempts >= MIN_SERVES)
      .sort((a, b) => ((b.serveAttempts - b.serveErrors) / b.serveAttempts) - ((a.serveAttempts - a.serveErrors) / a.serveAttempts))[0] ?? null;

    const passer = qualified
      .filter(p => p.receptions >= MIN_RECEIVES)
      .sort((a, b) => (b.receptionSum / b.receptions) - (a.receptionSum / a.receptions))[0] ?? null;

    return { teamStats, leaders: { hitter, server, passer } };
  }, [events, players, matchRecords]);

  const { teamStats, leaders } = statsData;
  const T = DEFAULT_STAT_THRESHOLDS;

  // Team W-L record (all completed games with an official match record or title-encoded result)
  const teamRecord = useMemo(() => {
    const opponentTeamIds = new Set(teams.filter(t => t.type === 'opponent').map(t => t.id));
    let wins = 0, losses = 0;
    for (const evt of events || []) {
      if (!isGameType(evt.type)) continue;
      if (evt.teamId && opponentTeamIds.has(evt.teamId)) continue;
      // Determine result: match record sets first, then title fallback
      const record = matchRecords.find(r => r.eventId === evt.id && r.isRecord);
      let result: 'W' | 'L' | null = null;
      if (record) {
        const { ourSetsWon, theirSetsWon } = record.gameState;
        if (ourSetsWon > theirSetsWon) result = 'W';
        else if (theirSetsWon > ourSetsWon) result = 'L';
      }
      if (!result) {
        const m = evt.title?.match(/\(([WL])\s+\d+-\d+\)/i);
        if (m) result = m[1].toUpperCase() as 'W' | 'L';
      }
      if (result === 'W') wins++;
      else if (result === 'L') losses++;
    }
    return { wins, losses };
  }, [events, matchRecords, teams]);

  // ── Billing ──────────────────────────────────────────────────────────────────
  const billing = currentOrg ? getBillingStatus(currentOrg, matchRecords) : null;
  const isBlocked = billing?.status === 'expired_trial' || billing?.status === 'expired_beta';
  const creditsUsed = (billing?.status === 'trial' || billing?.status === 'expired_trial')
    ? (billing as any).creditsUsed as number
    : 0;

  const handlePlayGame = (eventId: string) => {
    if (isBlocked) { setShowPaywall(true); return; }
    onPlayGame?.(eventId);
  };

  // Color helpers using default thresholds
  const heColor  = teamStats.hitEfficiency === null ? 'text-slate-400' : teamStats.hitEfficiency >= T.hitEfficiencyGreen ? 'text-green-600' : teamStats.hitEfficiency >= T.hitEfficiencyAmber ? 'text-amber-600' : 'text-red-500';
  const spColor  = teamStats.servePct      === null ? 'text-slate-400' : teamStats.servePct      >= T.servePctGreen      ? 'text-green-600' : teamStats.servePct      >= T.servePctAmber      ? 'text-amber-600' : 'text-red-500';
  const paColor  = teamStats.passAvg       === null ? 'text-slate-400' : teamStats.passAvg       >= T.passRatingGreen    ? 'text-green-600' : teamStats.passAvg       >= T.passRatingAmber    ? 'text-amber-600' : 'text-red-500';

  // Today's practice plan from localStorage
  const todaysPlan = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const orgKey = currentOrg?.id || 'default';
    try {
      const raw = localStorage.getItem(`practicePlan_${orgKey}_${today}`);
      if (!raw) return null;
      const blocks = JSON.parse(raw) as Array<{ id: string; title: string; drills: unknown[] }>;
      if (!Array.isArray(blocks) || blocks.length === 0) return null;
      const hasContent = blocks.some(b => b.drills && b.drills.length > 0);
      return hasContent ? blocks : null;
    } catch {
      return null;
    }
  }, [currentOrg?.id]);

  const hasAnyLeader = leaders.hitter || leaders.server || leaders.passer;

  return (
    <div className="space-y-8">
      {/* Org Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">{organizationName}</h1>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          <p className="text-slate-500">Dashboard</p>
          {(teamRecord.wins > 0 || teamRecord.losses > 0) && (
            <span className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-full px-3 py-0.5 text-sm font-bold text-slate-700">
              <Trophy className="w-3.5 h-3.5 text-amber-500" />
              {teamRecord.wins}–{teamRecord.losses}
            </span>
          )}
        </div>
      </div>

      {/* ── Billing Banners ── */}

      {/* Active Beta Banner */}
      {billing?.status === 'active_beta' && (
        <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
          <Zap className="w-4 h-4 text-indigo-500 shrink-0" />
          <p className="text-sm text-indigo-700 font-medium">
            <span className="font-bold">Beta Member:</span> Unlimited access until{' '}
            {formatDate((billing as any).expiresAt, { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      )}

      {/* Trial Credit Counter */}
      {(billing?.status === 'trial') && (
        <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <div className="flex items-center gap-3">
            <Shield className="w-4 h-4 text-amber-500 shrink-0" />
            <p className="text-sm text-amber-800 font-medium">
              <span className="font-bold">{creditsUsed}/{TRIAL_CREDIT_LIMIT} Match Credits Used</span>
              {' '}— each tracked game consumes one credit
            </p>
          </div>
          <div className="flex gap-1 shrink-0">
            {Array.from({ length: TRIAL_CREDIT_LIMIT }).map((_, i) => (
              <div
                key={i}
                className={`w-3 h-3 rounded-full ${i < creditsUsed ? 'bg-amber-500' : 'bg-amber-200'}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Trial Exhausted Banner */}
      {isBlocked && (
        <div className="flex items-center justify-between gap-3 bg-slate-100 border border-slate-300 rounded-xl px-4 py-3">
          <div className="flex items-center gap-3">
            <Lock className="w-4 h-4 text-slate-500 shrink-0" />
            <p className="text-sm text-slate-700 font-medium">
              <span className="font-bold">Trial Complete.</span> You've unlocked 3 full match profiles.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowPaywall(true)}
            className="shrink-0 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
          >
            Upgrade to Pro
          </button>
        </div>
      )}

      {/* Paywall Modal */}
      {showPaywall && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
            <button
              type="button"
              onClick={() => setShowPaywall(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-7 h-7 text-orange-500" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Trial Complete</h2>
              <p className="text-slate-500 text-sm leading-relaxed">
                You've unlocked 3 full match profiles. Upgrade to Pro to continue tracking your season — your data and analytics stay exactly as they are.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6 space-y-2">
              {[
                'Unlimited match tracking for the full season',
                'All Pro analytics & rotation breakdowns',
                'AI Practice Planner on every game',
                'HUDL sync & MaxPreps export',
                'Multi-device stat delegation',
              ].map(f => (
                <div key={f} className="flex items-center gap-2.5 text-sm text-slate-700">
                  <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-green-600 text-[9px] font-black">✓</span>
                  </div>
                  {f}
                </div>
              ))}
            </div>
            <a
              href="mailto:hello@sideoutiq.com?subject=Upgrade to Pro"
              className="block w-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-center py-3.5 rounded-xl transition-colors text-sm"
            >
              Upgrade to Pro — $49.99/yr
            </a>
            <p className="text-center text-slate-400 text-xs mt-3">
              Questions? Email us at hello@sideoutiq.com
            </p>
          </div>
        </div>
      )}

      {/* Pending Approvals Banner (admin only) */}
      {pendingApprovalsCount > 0 && (
        <button
          onClick={onGoToApprovals}
          className="w-full flex items-center justify-between gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 hover:bg-red-100 transition-colors text-left group"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-red-200 transition-colors">
              <Bell className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <span className="text-sm font-bold text-red-700">
                {pendingApprovalsCount} pending approval{pendingApprovalsCount !== 1 ? 's' : ''}
              </span>
              <p className="text-xs text-red-500">Coach join requests are waiting for your review</p>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-red-400 group-hover:text-red-600 shrink-0 transition-colors" />
        </button>
      )}

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* 1. Schedule & Up Next */}
        <div className="h-full min-h-[280px]">
            {nextEvent ? (
              <div
                onClick={() => handleEventClick(nextEvent)}
                className={`
                  h-full p-6 rounded-xl text-white shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.01] transition-all flex flex-col justify-between relative overflow-hidden
                  ${isGameType(nextEvent.type) ? 'bg-gradient-to-br from-indigo-600 to-indigo-800' : nextEvent.type === 'other' ? 'bg-gradient-to-br from-amber-500 to-amber-700' : 'bg-gradient-to-br from-emerald-600 to-emerald-800'}
                `}
              >
                 <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
                    {isGameType(nextEvent.type) ? <Trophy className="w-32 h-32" /> : nextEvent.type === 'other' ? <Calendar className="w-32 h-32" /> : <ClipboardList className="w-32 h-32" />}
                 </div>
                 <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded ${isGameType(nextEvent.type) ? 'bg-indigo-500/50' : nextEvent.type === 'other' ? 'bg-amber-400/50' : 'bg-emerald-500/50'}`}>
                        Up Next
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); changeView(ViewState.SCHEDULE); }}
                        className="text-white/80 hover:text-white p-1 rounded hover:bg-white/20 transition-colors"
                        title="View Full Schedule"
                      >
                        <Calendar className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="text-3xl font-bold mb-2 truncate leading-tight">{nextEvent.title}</div>
                    <div className="flex flex-col gap-2 text-sm opacity-90 mt-2">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span className="font-medium">{formatDateDisplay(nextEvent.date)} @ {nextEvent.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span className="truncate font-medium">{nextEvent.location}</span>
                      </div>
                    </div>
                 </div>
                 <div className="relative z-10 mt-8 flex items-center bg-white/20 w-fit px-4 py-2 rounded-lg backdrop-blur-sm font-bold text-sm hover:bg-white/30 transition-colors">
                   {isGameType(nextEvent.type) ? (
                     <>Start Game Mode <ArrowRight className="w-4 h-4 ml-2" /></>
                   ) : nextEvent.type === 'other' ? (
                     <>View Schedule <ArrowRight className="w-4 h-4 ml-2" /></>
                   ) : nextEvent.title.toLowerCase().includes('tryout') ? (
                     <>Manage Tryouts <ArrowRight className="w-4 h-4 ml-2" /></>
                   ) : (
                     <>View Practice Plan <ArrowRight className="w-4 h-4 ml-2" /></>
                   )}
                 </div>
              </div>
            ) : (
              <div
                onClick={() => changeView(ViewState.GAME)}
                className="h-full bg-slate-900 p-6 rounded-xl text-white shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.01] transition-all flex flex-col justify-between"
              >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium opacity-70 text-sm uppercase tracking-wider">No Events Scheduled</h3>
                      <button onClick={(e) => { e.stopPropagation(); changeView(ViewState.SCHEDULE); }} className="hover:text-slate-300"><Calendar className="w-5 h-5" /></button>
                    </div>
                    <div className="text-2xl font-bold mb-2">Start a Scrimmage</div>
                    <p className="text-slate-300 text-sm">
                      Jump straight into Game Time mode to track scores for an unscheduled match.
                    </p>
                  </div>
                  <div className="flex items-center text-white text-sm font-bold mt-6 bg-white/10 w-fit px-4 py-2 rounded-lg">
                     Go to Game Time <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
               </div>
            )}
        </div>

        {/* 2. Team Management */}
        <div
          onClick={() => changeView(ViewState.TEAM)}
          className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm cursor-pointer hover:shadow-md hover:border-blue-300 transition-all group flex flex-col justify-between h-full min-h-[280px]"
        >
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-slate-500 font-bold uppercase text-xs tracking-wider">Team Management</h3>
              <Users className="text-blue-500 w-5 h-5 group-hover:scale-110 transition-transform" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Manage Teams & Players</h2>
            <div className="space-y-3">
              {teams.length === 0 && getUnassignedCount() === 0 ? (
                 <div className="text-sm text-slate-400 italic">No teams setup yet.</div>
              ) : (
                <>
                  {teams.slice(0, 4).map(team => (
                    <div key={team.id} className="flex justify-between items-center text-sm">
                      <span className="font-medium text-slate-700 truncate">{team.name}</span>
                      <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-xs font-bold">
                        {getPlayerCount(team.id)} Players
                      </span>
                    </div>
                  ))}
                  {getUnassignedCount() > 0 && (
                     <div className="flex justify-between items-center text-sm border-t border-dashed border-slate-100 pt-2 mt-2">
                       <span className="font-medium text-slate-500 italic">Unassigned</span>
                       <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full text-xs font-bold">
                         {getUnassignedCount()} Players
                       </span>
                     </div>
                  )}
                  {teams.length > 4 && (
                    <div className="text-xs text-blue-600 font-medium pt-1">
                      + {teams.length - 4} more teams
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center text-blue-600 text-sm font-bold mt-6 group-hover:translate-x-1 transition-transform">
             View Roster <ArrowRight className="w-4 h-4 ml-1" />
          </div>
        </div>

        {/* 3. Practice Hub */}
        <div
            onClick={() => changeView(ViewState.PRACTICE)}
            className="bg-white rounded-xl border border-slate-200 p-6 flex flex-col justify-between items-center text-center shadow-sm cursor-pointer hover:shadow-md hover:border-indigo-300 transition-all group h-full min-h-[280px]"
        >
          {todaysPlan ? (
            <>
              <div className="w-full text-left">
                <div className="flex items-center gap-2 mb-3">
                  <ClipboardList className="w-5 h-5 text-indigo-600 shrink-0" />
                  <h3 className="font-bold text-lg text-slate-900">Today's Practice</h3>
                </div>
                <div className="space-y-1.5 mb-6">
                  {todaysPlan.slice(0, 4).map(block => (
                    <div key={block.id} className="flex items-center justify-between bg-indigo-50 rounded-lg px-3 py-2">
                      <span className="text-sm font-medium text-slate-700 truncate">{block.title || 'Untitled Block'}</span>
                      <span className="text-xs text-indigo-600 font-bold ml-2 shrink-0">
                        {block.drills.length} drill{block.drills.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                  {todaysPlan.length > 4 && (
                    <div className="text-xs text-slate-400 text-center pt-1">+ {todaysPlan.length - 4} more blocks</div>
                  )}
                </div>
              </div>
              <button className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-indigo-700 transition-colors w-full shadow-sm">
                Go to Plan
              </button>
            </>
          ) : (
            <>
              <div className="bg-indigo-50 p-6 rounded-full mb-6 group-hover:scale-110 transition-transform">
                <ClipboardList className="w-10 h-10 text-indigo-600" />
              </div>
              <div className="w-full">
                <h3 className="font-bold text-2xl mb-2 text-slate-900">Practice Hub</h3>
                <p className="text-slate-500 text-sm max-w-xs mx-auto mb-6">Manual and AI assisted practice plan creation.</p>
                <button className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-indigo-700 transition-colors w-full shadow-sm">
                  Create Plan
                </button>
              </div>
            </>
          )}
        </div>

        {/* 4. Stats Widget */}
        <div
          onClick={() => changeView(ViewState.STATS)}
          className="bg-white rounded-xl border border-slate-200 p-6 flex flex-col gap-5 cursor-pointer hover:border-blue-300 hover:shadow-md transition-all h-full min-h-[280px]"
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-blue-600" />
              <h3 className="font-bold text-slate-800">Season Stats</h3>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" />
          </div>

          {!teamStats.hasAnyData ? (
            <div className="text-sm text-slate-400 italic py-8 text-center">No stats recorded yet.</div>
          ) : (
            <>
              {/* Team Stats Summary */}
              <div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Team Summary</div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-slate-50 rounded-lg p-2.5 text-center border border-slate-100">
                    <div className={`text-base font-black leading-none ${heColor}`}>
                      {teamStats.hitEfficiency === null ? '—' : `${teamStats.hitEfficiency >= 0 ? '' : ''}${(teamStats.hitEfficiency * 100).toFixed(1)}%`}
                    </div>
                    <div className="text-xs text-slate-400 mt-1 font-medium">Hit Eff</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2.5 text-center border border-slate-100">
                    <div className={`text-base font-black leading-none ${spColor}`}>
                      {teamStats.servePct === null ? '—' : `${(teamStats.servePct * 100).toFixed(1)}%`}
                    </div>
                    <div className="text-xs text-slate-400 mt-1 font-medium">Serve %</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2.5 text-center border border-slate-100">
                    <div className={`text-base font-black leading-none ${paColor}`}>
                      {teamStats.passAvg === null ? '—' : teamStats.passAvg.toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-400 mt-1 font-medium">Pass Avg</div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-slate-100" />

              {/* Player Leaders */}
              <div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Player Leaders <span className="normal-case font-normal">(min. 20 attempts)</span></div>
                {!hasAnyLeader ? (
                  <div className="text-xs text-slate-400 italic">Not enough attempts yet — leaders appear after 20+ attempts.</div>
                ) : (
                  <div className="space-y-3">
                    {leaders.hitter && (() => {
                      const h = (leaders.hitter.kills - leaders.hitter.attackErrors) / leaders.hitter.attackAttempts;
                      return (
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100 shrink-0">
                            <Activity className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] text-slate-400 uppercase font-bold">Hit Eff Leader</div>
                            <div className="text-sm font-bold text-slate-900 truncate">{leaders.hitter.player.name}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className={`text-base font-black leading-none ${h >= T.hitEfficiencyGreen ? 'text-green-600' : h >= T.hitEfficiencyAmber ? 'text-amber-600' : 'text-red-500'}`}>
                              {h >= 0 ? '' : ''}{(h * 100).toFixed(1)}%
                            </div>
                            <div className="text-[10px] text-slate-400">{leaders.hitter.attackAttempts} att</div>
                          </div>
                        </div>
                      );
                    })()}

                    {leaders.server && (() => {
                      const s = (leaders.server.serveAttempts - leaders.server.serveErrors) / leaders.server.serveAttempts;
                      return (
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center text-green-600 border border-green-100 shrink-0">
                            <Zap className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] text-slate-400 uppercase font-bold">Serve % Leader</div>
                            <div className="text-sm font-bold text-slate-900 truncate">{leaders.server.player.name}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className={`text-base font-black leading-none ${s >= T.servePctGreen ? 'text-green-600' : s >= T.servePctAmber ? 'text-amber-600' : 'text-red-500'}`}>
                              {(s * 100).toFixed(1)}%
                            </div>
                            <div className="text-[10px] text-slate-400">{leaders.server.serveAttempts} serves</div>
                          </div>
                        </div>
                      );
                    })()}

                    {leaders.passer && (() => {
                      const avg = leaders.passer.receptionSum / leaders.passer.receptions;
                      return (
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center text-purple-600 border border-purple-100 shrink-0">
                            <Shield className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] text-slate-400 uppercase font-bold">Pass Avg Leader</div>
                            <div className="text-sm font-bold text-slate-900 truncate">{leaders.passer.player.name}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className={`text-base font-black leading-none ${avg >= T.passRatingGreen ? 'text-green-600' : avg >= T.passRatingAmber ? 'text-amber-600' : 'text-red-500'}`}>
                              {avg.toFixed(2)}
                            </div>
                            <div className="text-[10px] text-slate-400">{leaders.passer.receptions} rec</div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
